import React, { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';

import LabeledContainer from '../../../../../molecules/common/LabeledContainer';
import BooksNotes from '../../../../../molecules/merchant/resources/book/BooksNotes';
import CheckInput, { InputType } from '../../../../../atoms/CheckInput';
import BooksConfirmAlert from '../../../../../molecules/merchant/resources/book/BooksConfirmAlert';
import BooksTermsOfUse from '../../../../../molecules/merchant/resources/book/BooksTermsOfUse';
import BooksSchoolItemList from '../../../../../molecules/merchant/resources/book/BooksSchoolItemList';

import { SettingConfirmation } from '../../../../../interfaces/merchants/resources/books';
import { useResourceNotes } from '../../../../../helpers/merchants/resources/helper';
import { scrollToTop } from '../../../../../hooks/useScrollToTop';
import { useShallowEqualSelector } from '../../../../../hooks/redux';

import {
  inputConfirmation,
  ReinputPaymentError,
  ReselectDateError,
  RestartReservationError,
} from '../../../../../redux/actions/merchants/resources/books/inputs';
import { ReduxState } from '../../../../../redux/reducers';
import { getMerchantResourcesBooksInputs } from '../../../../../redux/selectors/merchants/resources/books/inputs';
import { useLineLiffAccessToken } from '../../../../../hooks/line-liff';
import css from './style.module.scss';
import { useAppDispatch } from '../../../../../redux';
import { RSButton } from 'src/reservation-page/RSButton';
import { buildSiteUrl } from 'src/utils/request-url';

interface ElementProps {
  merchantResource: { publicId: string; name: string; imageUrl?: string };
  settings: SettingConfirmation;
  postable: boolean;
  onSubmit: () => void;
  onReinputPayment: () => void;
  onReselect: () => void;
  onRestart: () => void;
  publicId: string;
  lineLiffId: string | undefined;
}

type Props = ElementProps;

const InputConfirmationSchool: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const { merchantResource, settings, postable, lineLiffId } = props;
  const [isTermAccepted, setIsTermAccepted] = useState<boolean | undefined>(
    false,
  );

  const useSelector = () =>
    useShallowEqualSelector((state: ReduxState) => ({
      inputs: getMerchantResourcesBooksInputs(
        state.merchantsResourcesBooksInputs,
      ),
    }));
  const { inputs } = useSelector();
  const notes = useResourceNotes(settings);

  useEffect(() => {
    scrollToTop();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCheck = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.currentTarget;
    setIsTermAccepted(checked);
  }, []);

  const { lineAccessToken, lineIdToken } = useLineLiffAccessToken(lineLiffId);
  const handleSubmit = useCallback(async () => {
    const {
      onSubmit,
      onReinputPayment,
      onReselect,
      onRestart,
      settings: { selectedPaymentMethod },
      publicId,
    } = props;

    try {
      await dispatch(
        inputConfirmation({
          reservationId: inputs.reservationId!,
          termsAccepted: isTermAccepted,
          selectedPaymentMethod,
          lineAccessToken,
          lineIdToken,
          merchantPublicId: publicId,
        }),
      );
      onSubmit();
    } catch (e) {
      if (e instanceof ReselectDateError) {
        onReselect();
      } else if (e instanceof RestartReservationError) {
        onRestart();
      } else if (e instanceof ReinputPaymentError) {
        onReinputPayment();
      }
    }
  }, [
    dispatch,
    props,
    inputs.reservationId,
    isTermAccepted,
    lineAccessToken,
    lineIdToken,
  ]);

  const intl = useIntl();
  const { resourceTerm } = settings;
  const reservations = inputs;
  const schoolTimeSlot = reservations.schoolTimeSlots![0];

  return (
    <div>
      <BooksConfirmAlert
        termsUrl="https://stores.fun/legal/reserve-user-terms"
        lawUrl={buildSiteUrl(`/legal_notice`)}
      />
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.booksItems',
        })}
        paddingTop={true}
      >
        {schoolTimeSlot && (
          <BooksSchoolItemList
            nickname={props.publicId}
            resourceId={merchantResource.publicId}
            resourceName={merchantResource.name}
            resourceImage={merchantResource.imageUrl ?? ''}
            startTime={schoolTimeSlot.startTime}
            endTime={schoolTimeSlot.endTime}
            instructors={schoolTimeSlot.instructors.map((i) => i.name)}
            date={schoolTimeSlot.date}
            lessonName={reservations.lesson ? reservations.lesson.name : ''}
          />
        )}
      </LabeledContainer>
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.attentionForBooking',
        })}
        contentsPadding={true}
        paddingTop={true}
      >
        <BooksNotes notes={notes} />
      </LabeledContainer>
      {resourceTerm && (
        <>
          <LabeledContainer
            label={intl.formatMessage({
              id: 'books.formsLabel.termsOfService',
            })}
            contentsPadding={true}
            paddingTop={true}
          >
            <BooksTermsOfUse text={resourceTerm} />
          </LabeledContainer>
          <LabeledContainer
            label={intl.formatMessage({
              id: 'books.formsLabel.needAgree',
            })}
          >
            <CheckInput
              type={InputType.Checkbox}
              onChange={handleCheck}
              name="terms-of-use"
              value="agree"
            >
              {intl.formatMessage({
                id: 'books.forms.agreeTermsOfService',
              })}
            </CheckInput>
          </LabeledContainer>
        </>
      )}
      <div className={css.submit_button}>
        <RSButton
          variant="basic"
          isBlock
          isLoading={!postable}
          disabled={(!!resourceTerm && !isTermAccepted) || !postable}
          type="button"
          value={intl.formatMessage({
            id: 'books.submitLabel.reserve',
          })}
          onClick={handleSubmit}
        >
          {intl.formatMessage({
            id: 'books.submitLabel.reserve',
          })}
        </RSButton>
      </div>
    </div>
  );
};

export default InputConfirmationSchool;
